import * as Api from '@ViewModels';

export type TimelineRow =
	| { kind: 'speaker'; offset: number; speaker: string; line: string }
	| { kind: 'ai'; offset: number; role: Api.AIChatRole; text: string; messageId: string };

interface MergeParams {
	speakerLines: Api.ISpeakerLine[] | undefined;
	aiMessages: Api.AIChatMessage[] | undefined;
	callStartTime: string | undefined;
	callDuration: number | undefined;
}

const messageText = (m: Api.AIChatMessage): string => {
	if (m.text && m.text.trim().length > 0) return m.text;
	const flattened = (m.contents ?? [])
		.map(c => (c as { text?: string }).text ?? '')
		.filter(Boolean)
		.join('\n')
		.trim();
	return flattened;
};

type CoachingMessage = Api.AIChatMessage & { role: Api.AIChatRole };
const isCoachingRole = (m: Api.AIChatMessage): m is CoachingMessage =>
	m.role === Api.AIChatRole.Assistant || m.role === Api.AIChatRole.User;

export const mergeAdherenceTimeline = ({
	speakerLines,
	aiMessages,
	callStartTime,
	callDuration,
}: MergeParams): TimelineRow[] => {
	const speakerRows: TimelineRow[] = (speakerLines ?? []).map(line => ({
		kind: 'speaker',
		offset: line.startTimeOffset,
		speaker: line.speaker,
		line: line.line,
	}));

	const startMs = callStartTime ? new Date(callStartTime).getTime() : undefined;
	const tailOffset = callDuration ?? Number.MAX_SAFE_INTEGER;

	const aiRows: TimelineRow[] = (aiMessages ?? [])
		.filter(isCoachingRole)
		.map((m): TimelineRow | null => {
			const text = messageText(m);
			if (!text) return null;
			let offset: number;
			if (startMs !== undefined && m.creationDate) {
				const raw = (new Date(m.creationDate).getTime() - startMs) / 1000;
				offset = Math.max(0, callDuration !== undefined ? Math.min(raw, callDuration) : raw);
			} else {
				offset = tailOffset;
			}
			return {
				kind: 'ai',
				offset,
				role: m.role,
				text,
				messageId: m.id,
			};
		})
		.filter((r): r is TimelineRow => r !== null);

	// Speaker rows are concatenated first so equal-offset ties stably render the AI row beneath the human line it followed.
	return [...speakerRows, ...aiRows].sort((a, b) => a.offset - b.offset);
};
